import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText, Image, Send, Copy, Trash2, Eye, ExternalLink, Upload, Plus, X, Check, Loader2, Video, Download, ChevronRight, Sparkles, Phone, Music2, Headphones, Camera, Film, Heart, Star, Gem, UtensilsCrossed, Music4 } from 'lucide-react';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

// Icon mapping
const ICONS = {
  headphones: Headphones,
  music2: Music2,
  music4: Music4,
  camera: Camera,
  video: Film,
  sparkles: Sparkles,
  gem: Gem,
  utensils: UtensilsCrossed,
  heart: Heart,
  star: Star,
  phone: Phone,
};

const DossierGenerator = ({ onClose }) => {
  // Tab state
  const [activeTab, setActiveTab] = useState('create');
  
  // Services & selection state
  const [services, setServices] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [presets, setPresets] = useState([]);
  
  // Client info state
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  
  // Photos & Videos state
  const [photos, setPhotos] = useState([]);
  const [videos, setVideos] = useState([]);
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [selectedVideos, setSelectedVideos] = useState([]);
  
  // Upload state
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [newVideoUrl, setNewVideoUrl] = useState('');
  const [newVideoTitle, setNewVideoTitle] = useState('');
  const [selectedPhotoCategory, setSelectedPhotoCategory] = useState('event');
  const fileInputRef = useRef(null);
  
  // Generated dossiers state
  const [recentDossiers, setRecentDossiers] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [loading, setLoading] = useState(true);

  // Photo categories
  const PHOTO_CATEGORIES = [
    { id: 'coctel', name: 'Cóctel' },
    { id: 'dj', name: 'DJ' },
    { id: 'saxo', name: 'Saxo' },
    { id: '360', name: '360°' },
    { id: 'fotomaton', name: 'Fotomatón' },
    { id: 'audiolibro', name: 'Audiolibro' },
    { id: 'baile', name: 'Baile' },
    { id: 'extras', name: 'Extras' },
    { id: 'boda_completa', name: 'Boda' },
    { id: 'event', name: 'General' },
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [servicesRes, presetsRes, photosRes, dossiersRes] = await Promise.all([
        axios.get(`${API}/api/dossier/services`),
        axios.get(`${API}/api/dossier/presets`),
        axios.get(`${API}/api/dossier/photos`),
        axios.get(`${API}/api/dossier/recent?limit=20`),
      ]);
      
      setServices(servicesRes.data.services || []);
      setPresets(presetsRes.data.presets || []);
      setPhotos(photosRes.data.photos || []);
      setRecentDossiers(dossiersRes.data.dossiers || []);
      
      // Extract videos from services
      const allVideos = [];
      const videoIds = new Set();
      (servicesRes.data.services || []).forEach(service => {
        (service.videos || []).forEach(videoId => {
          if (!videoIds.has(videoId)) {
            videoIds.add(videoId);
            // We'll get video details from config
          }
        });
      });
      
      // Get video details from config endpoint
      try {
        const configRes = await axios.get(`${API}/api/dossier/config`);
        setVideos(configRes.data.config?.videos || []);
      } catch (e) {
        console.log('Config not available, using defaults');
      }
      
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error cargando datos');
    } finally {
      setLoading(false);
    }
  };

  // Toggle service selection
  const toggleService = (serviceId) => {
    setSelectedServices(prev => 
      prev.includes(serviceId) 
        ? prev.filter(id => id !== serviceId)
        : [...prev, serviceId]
    );
  };

  // Apply preset
  const applyPreset = (preset) => {
    setSelectedServices(preset.services || []);
    toast.success(`Preset "${preset.name}" aplicado`);
  };

  // Toggle photo selection
  const togglePhoto = (photoId) => {
    setSelectedPhotos(prev =>
      prev.includes(photoId)
        ? prev.filter(id => id !== photoId)
        : [...prev, photoId]
    );
  };

  // Toggle video selection
  const toggleVideo = (videoId) => {
    setSelectedVideos(prev =>
      prev.includes(videoId)
        ? prev.filter(id => id !== videoId)
        : [...prev, videoId]
    );
  };

  // Upload photo
  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setUploadingPhoto(true);
    
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await axios.post(
          `${API}/api/dossier/photos/upload?category=${selectedPhotoCategory}&title=${encodeURIComponent(file.name.replace(/\.[^/.]+$/, ''))}`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        
        if (response.data.success) {
          setPhotos(prev => [...prev, response.data.photo]);
          setSelectedPhotos(prev => [...prev, response.data.photo.id]);
          toast.success(`Foto "${file.name}" subida`);
        }
      } catch (error) {
        console.error('Error uploading:', error);
        toast.error(`Error subiendo ${file.name}`);
      }
    }
    
    setUploadingPhoto(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Delete photo
  const deletePhoto = async (photoId) => {
    try {
      await axios.delete(`${API}/api/dossier/photos/${photoId}`);
      setPhotos(prev => prev.filter(p => p.id !== photoId));
      setSelectedPhotos(prev => prev.filter(id => id !== photoId));
      toast.success('Foto eliminada');
    } catch (error) {
      toast.error('Error eliminando foto');
    }
  };

  // Add video link
  const addVideoLink = () => {
    if (!newVideoUrl.trim()) return;
    
    const videoId = `custom_${Date.now()}`;
    const newVideo = {
      id: videoId,
      url: newVideoUrl.trim(),
      title: newVideoTitle.trim() || 'Video personalizado',
      category: 'custom',
      is_short: newVideoUrl.includes('shorts')
    };
    
    setVideos(prev => [...prev, newVideo]);
    setSelectedVideos(prev => [...prev, videoId]);
    setNewVideoUrl('');
    setNewVideoTitle('');
    toast.success('Video añadido');
  };

  // Generate dossier
  const generateDossier = async () => {
    if (selectedServices.length === 0) {
      toast.error('Selecciona al menos un servicio');
      return;
    }

    setGenerating(true);
    
    try {
      // Obtener videos custom para enviar al backend
      const customVideos = videos.filter(v => v.id.startsWith('custom_') && selectedVideos.includes(v.id));
      
      const response = await axios.post(`${API}/api/dossier/generate`, {
        services: selectedServices,
        client_name: clientName.trim() || null,
        custom_message: customMessage.trim() || null,
        selected_photos: selectedPhotos,
        selected_videos: selectedVideos,
        custom_videos: customVideos,  // Enviar datos de videos personalizados
      });

      if (response.data.success) {
        toast.success('Dossier generado correctamente');
        
        // Refresh recent dossiers
        const dossiersRes = await axios.get(`${API}/api/dossier/recent?limit=20`);
        setRecentDossiers(dossiersRes.data.dossiers || []);
        
        // Switch to recent tab
        setActiveTab('recent');
        
        // Reset form
        setClientName('');
        setCustomMessage('');
        setSelectedPhotos([]);
        setSelectedVideos([]);
      }
    } catch (error) {
      console.error('Error generating dossier:', error);
      toast.error('Error generando dossier');
    } finally {
      setGenerating(false);
    }
  };

  // Copy dossier link
  const copyDossierLink = (token) => {
    const url = `${window.location.origin}/dossier/${token}`;
    navigator.clipboard.writeText(url);
    toast.success('Enlace copiado al portapapeles');
  };

  // Send via WhatsApp
  const sendWhatsApp = (token, phone) => {
    const url = `${window.location.origin}/dossier/${token}`;
    const message = encodeURIComponent(`¡Hola! Te envío información sobre nuestros servicios para tu evento:\n\n${url}\n\n¿Tienes alguna duda?`);
    const whatsappUrl = phone 
      ? `https://wa.me/${phone.replace(/\D/g, '')}?text=${message}`
      : `https://wa.me/?text=${message}`;
    window.open(whatsappUrl, '_blank');
  };

  // Download PDF
  const downloadPDF = async (token, clientName) => {
    try {
      toast.info('Generando PDF...');
      
      const response = await fetch(`${API}/api/dossier/pdf/${token}`);
      
      if (!response.ok) {
        throw new Error('Error al generar PDF');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Dossier_${clientName || 'Cliente'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF descargado');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Error al descargar PDF');
    }
  };

  // Delete dossier
  const deleteDossier = async (token) => {
    try {
      await axios.delete(`${API}/api/dossier/${token}`);
      setRecentDossiers(prev => prev.filter(d => d.token !== token));
      toast.success('Dossier eliminado');
    } catch (error) {
      toast.error('Error eliminando dossier');
    }
  };

  // Get icon component
  const getIcon = (iconName) => ICONS[iconName] || Star;

  // Get photos by category
  const getPhotosByCategory = (category) => photos.filter(p => p.category === category);

  if (loading) {
    return (
      <div className="bg-slate-900 rounded-xl p-8 w-full max-w-4xl mx-auto flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="bg-slate-900 rounded-xl overflow-hidden w-full max-w-4xl mx-auto max-h-[85vh] flex flex-col">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-600 to-orange-600 p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FileText className="w-6 h-6 text-white" />
          <h2 className="text-xl font-bold text-white">Generador de Dossiers</h2>
        </div>
        {onClose && (
          <button onClick={onClose} className="text-white/80 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
        <TabsList className="bg-slate-800 p-1 mx-4 mt-4 rounded-lg grid grid-cols-3">
          <TabsTrigger value="create" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white rounded-md">
            <FileText className="w-4 h-4 mr-2" />
            Crear
          </TabsTrigger>
          <TabsTrigger value="media" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white rounded-md">
            <Image className="w-4 h-4 mr-2" />
            Fotos y Videos
          </TabsTrigger>
          <TabsTrigger value="recent" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white rounded-md">
            <Send className="w-4 h-4 mr-2" />
            Enviados
          </TabsTrigger>
        </TabsList>

        <div className="flex-1 overflow-y-auto p-4">
          {/* TAB 1: CREATE */}
          <TabsContent value="create" className="mt-0 space-y-6">
            {/* Client Info */}
            <div className="bg-slate-800/50 rounded-lg p-4 space-y-3">
              <h3 className="text-sm font-semibold text-amber-400 uppercase tracking-wider">Información del Cliente</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input
                  type="text"
                  placeholder="Nombre del cliente (opcional)"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-amber-500"
                />
                <input
                  type="tel"
                  placeholder="Teléfono WhatsApp (opcional)"
                  value={clientPhone}
                  onChange={(e) => setClientPhone(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-amber-500"
                />
              </div>
              <textarea
                placeholder="Mensaje personalizado (opcional)"
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-amber-500 resize-none"
              />
            </div>

            {/* Presets */}
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-amber-400 uppercase tracking-wider">Presets Rápidos</h3>
              <div className="flex flex-wrap gap-2">
                {presets.map(preset => (
                  <button
                    key={preset.id}
                    onClick={() => applyPreset(preset)}
                    className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm rounded-lg transition-colors"
                  >
                    {preset.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Services Selection */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-amber-400 uppercase tracking-wider">
                Servicios ({selectedServices.length} seleccionados)
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {services.map(service => {
                  const Icon = getIcon(service.icon);
                  const isSelected = selectedServices.includes(service.id);
                  
                  return (
                    <button
                      key={service.id}
                      onClick={() => toggleService(service.id)}
                      className={`p-3 rounded-lg border-2 transition-all text-left flex items-start gap-3 ${
                        isSelected 
                          ? 'border-amber-500 bg-amber-500/10' 
                          : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        isSelected ? 'bg-amber-500' : 'bg-slate-700'
                      }`}>
                        <Icon className={`w-5 h-5 ${isSelected ? 'text-white' : 'text-slate-400'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className={`font-semibold text-sm ${isSelected ? 'text-amber-400' : 'text-white'}`}>
                          {service.name}
                        </h4>
                        {service.subtitle && (
                          <p className="text-xs text-slate-500 italic truncate">{service.subtitle}</p>
                        )}
                      </div>
                      {isSelected && <Check className="w-5 h-5 text-amber-500 flex-shrink-0" />}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Next step indicator */}
            <div className="flex justify-center pt-4">
              <button
                onClick={() => setActiveTab('media')}
                className="flex items-center gap-2 px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
              >
                Siguiente: Fotos y Videos
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </TabsContent>

          {/* TAB 2: MEDIA (Photos & Videos) */}
          <TabsContent value="media" className="mt-0 space-y-6">
            {/* Photos Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-amber-400 uppercase tracking-wider">
                  Fotos ({selectedPhotos.length} seleccionadas)
                </h3>
                <div className="flex items-center gap-2">
                  <select
                    value={selectedPhotoCategory}
                    onChange={(e) => setSelectedPhotoCategory(e.target.value)}
                    className="px-2 py-1 bg-slate-700 border border-slate-600 rounded text-sm text-white"
                  >
                    {PHOTO_CATEGORIES.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handlePhotoUpload}
                    className="hidden"
                  />
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingPhoto}
                    size="sm"
                    className="bg-amber-600 hover:bg-amber-700"
                  >
                    {uploadingPhoto ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    <span className="ml-1">Subir</span>
                  </Button>
                </div>
              </div>

              {/* Photos by category */}
              {PHOTO_CATEGORIES.map(category => {
                const categoryPhotos = getPhotosByCategory(category.id);
                if (categoryPhotos.length === 0) return null;
                
                return (
                  <div key={category.id} className="space-y-2">
                    <h4 className="text-xs font-medium text-slate-400">{category.name}</h4>
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                      {categoryPhotos.map(photo => {
                        const isSelected = selectedPhotos.includes(photo.id);
                        return (
                          <div
                            key={photo.id}
                            className={`relative aspect-square rounded-lg overflow-hidden cursor-pointer border-2 transition-all ${
                              isSelected ? 'border-amber-500 ring-2 ring-amber-500/30' : 'border-transparent hover:border-slate-600'
                            }`}
                            onClick={() => togglePhoto(photo.id)}
                          >
                            <img
                              src={`${API}${photo.url}`}
                              alt={photo.title}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                            {isSelected && (
                              <div className="absolute inset-0 bg-amber-500/30 flex items-center justify-center">
                                <Check className="w-8 h-8 text-white" />
                              </div>
                            )}
                            <button
                              onClick={(e) => { e.stopPropagation(); deletePhoto(photo.id); }}
                              className="absolute top-1 right-1 p-1 bg-red-600 rounded-full opacity-0 hover:opacity-100 transition-opacity"
                            >
                              <Trash2 className="w-3 h-3 text-white" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              {photos.length === 0 && (
                <div className="text-center py-8 text-slate-500">
                  <Image className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No hay fotos. Sube algunas para incluirlas en el dossier.</p>
                </div>
              )}
            </div>

            {/* Videos Section */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-amber-400 uppercase tracking-wider">
                Videos ({selectedVideos.length} seleccionados)
              </h3>
              
              {/* Add video link */}
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="URL de YouTube o Vimeo"
                  value={newVideoUrl}
                  onChange={(e) => setNewVideoUrl(e.target.value)}
                  className="flex-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm placeholder-slate-400"
                />
                <input
                  type="text"
                  placeholder="Título (opcional)"
                  value={newVideoTitle}
                  onChange={(e) => setNewVideoTitle(e.target.value)}
                  className="w-40 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm placeholder-slate-400"
                />
                <Button onClick={addVideoLink} size="sm" className="bg-amber-600 hover:bg-amber-700">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>

              {/* Video grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {videos.map(video => {
                  const isSelected = selectedVideos.includes(video.id);
                  const thumbnail = video.url.includes('youtube') || video.url.includes('youtu.be')
                    ? `https://img.youtube.com/vi/${video.url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([^?&]+)/)?.[1]}/mqdefault.jpg`
                    : null;
                  
                  return (
                    <div
                      key={video.id}
                      onClick={() => toggleVideo(video.id)}
                      className={`relative aspect-video rounded-lg overflow-hidden cursor-pointer border-2 transition-all ${
                        isSelected ? 'border-amber-500 ring-2 ring-amber-500/30' : 'border-transparent hover:border-slate-600'
                      }`}
                    >
                      {thumbnail ? (
                        <img src={thumbnail} alt={video.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-slate-700 to-slate-800 flex items-center justify-center">
                          <Video className="w-8 h-8 text-slate-500" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        {isSelected ? (
                          <Check className="w-8 h-8 text-amber-500" />
                        ) : (
                          <Video className="w-6 h-6 text-white/60" />
                        )}
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 p-1.5 bg-gradient-to-t from-black/80">
                        <p className="text-xs text-white truncate">{video.title}</p>
                      </div>
                      {video.is_short && (
                        <span className="absolute top-1 right-1 px-1.5 py-0.5 bg-red-500 text-white text-xs rounded">Short</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Generate button */}
            <div className="flex justify-center pt-4 border-t border-slate-700">
              <Button
                onClick={generateDossier}
                disabled={generating || selectedServices.length === 0}
                className="px-8 py-3 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-semibold rounded-lg"
              >
                {generating ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Generando...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    Crear Dossier ({selectedServices.length} servicios, {selectedPhotos.length} fotos, {selectedVideos.length} videos)
                  </>
                )}
              </Button>
            </div>
          </TabsContent>

          {/* TAB 3: RECENT/SENT */}
          <TabsContent value="recent" className="mt-0 space-y-4">
            <h3 className="text-sm font-semibold text-amber-400 uppercase tracking-wider">
              Dossiers Enviados ({recentDossiers.length})
            </h3>
            
            {recentDossiers.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Send className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No hay dossiers generados todavía</p>
                <p className="text-sm mt-1">Crea uno en la pestaña "Crear"</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentDossiers.map(dossier => (
                  <div
                    key={dossier.token}
                    className="bg-slate-800/50 border border-slate-700 rounded-lg p-4"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-semibold text-white truncate">
                            {dossier.client_name || 'Sin nombre'}
                          </h4>
                          <span className="text-xs text-slate-500">
                            {dossier.services?.length || 0} servicios
                          </span>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-slate-400">
                          <span>{new Date(dossier.created_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                          <span className="flex items-center gap-1">
                            <Eye className="w-3 h-3" />
                            {dossier.views || 0} vistas
                          </span>
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => copyDossierLink(dossier.token)}
                          className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
                          title="Copiar enlace"
                        >
                          <Copy className="w-4 h-4 text-slate-400" />
                        </button>
                        <button
                          onClick={() => window.open(`/dossier/${dossier.token}`, '_blank')}
                          className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
                          title="Ver dossier"
                        >
                          <ExternalLink className="w-4 h-4 text-slate-400" />
                        </button>
                        <button
                          onClick={() => sendWhatsApp(dossier.token, dossier.client_phone)}
                          className="p-2 hover:bg-green-700 bg-green-600/20 rounded-lg transition-colors"
                          title="Enviar por WhatsApp"
                        >
                          <Phone className="w-4 h-4 text-green-400" />
                        </button>
                        <button
                          onClick={() => downloadPDF(dossier.token, dossier.client_name)}
                          className="p-2 hover:bg-blue-700 bg-blue-600/20 rounded-lg transition-colors"
                          title="Descargar PDF"
                        >
                          <Download className="w-4 h-4 text-blue-400" />
                        </button>
                        <button
                          onClick={() => deleteDossier(dossier.token)}
                          className="p-2 hover:bg-red-700 bg-red-600/20 rounded-lg transition-colors"
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
};

export default DossierGenerator;
